/**
 * These are the *presentational* prop shapes used across Vault components — the
 * vocabulary the design models. They are intentionally decoupled from any
 * GraphQL-generated types: containers map server data onto these shapes and pass
 * them to the components, so the components stay framework-agnostic and testable.
 *
 * Mirrors the data model in the Document Vault design (`window.DV.*`).
 *
 * MODEL
 * ---------------------------------------------------------------------------
 * The Vault is ONE queryable universe. Every document belongs to a {@link Space}
 * — a bounded context that owns (1) a set of REFERENCED attributes (global +
 * space-local), (2) validation rules over them, (3) a write/permission scope, and
 * (4) a quality/lifecycle {@link SpaceContract}. A {@link VaultDomain} (Fund Admin,
 * DEX, Fay, Agency) is just a filter tag on a space, not a partition.
 *
 * Attributes are GOVERNED. {@link GlobalAttribute}s are a curated shared spine —
 * only a steward can mint one; everyone else references them. {@link
 * SpaceLocalAttribute}s are created freely by a space configurator and are
 * sandboxed to a single space. Spaces never fork an attribute set — they reference
 * and extend via {@link GlobalRef}.
 */

/** Whether a document may leave the organization. */
export type DocumentStatus = "Internal" | "External";

/**
 * The backend's filing lifecycle, distinct from the Internal/External SHARING
 * status above. Set by `finalizeVaultDocument`'s guaranteed-attribute gate:
 * `pending` = bytes not finalized yet, `validated` = every guaranteed attribute
 * present, `quarantined` = something required is missing.
 */
export type DocumentLifecycle = "pending" | "validated" | "quarantined";

/** Display label for a filing lifecycle value. */
export const DOCUMENT_LIFECYCLE_LABELS: Record<DocumentLifecycle, string> = {
  pending: "Pending",
  validated: "Filed",
  quarantined: "Awaiting filing",
};

/** A single revision of a document, newest first in `VaultDocument.versions`. */
export type DocumentVersion = {
  /** Version label, e.g. "v2". */
  n: string;
  note: string;
  who: string;
  /** Uploader role/title, e.g. "Fund Admin", shown alongside who when present. */
  role?: string;
  /** Display date, e.g. "Aug 8, 2024". */
  date: string;
  /** Uploaded file name, when a version came from a new upload. */
  fileName?: string;
};

/** Global-attribute values on a document, keyed by {@link GlobalAttribute.key}. */
export type GlobalAttrValues = Record<string, string>;

/** Space-local attribute values on a document, keyed by attribute name. */
export type SpaceLocalValues = Record<string, string>;

/** A document record as the Vault knows it — structured metadata, no file contents. */
export type VaultDocument = {
  id: string;
  /** File kind / extension, e.g. "pdf", "xls" — pass to @jsq/ds FileIcon. */
  file: string;
  name: string;
  /** {@link Space} id the document belongs to. */
  space: string;
  /** {@link VaultDomain} id (filter tag), derivable from the space. */
  domain: string;
  status: DocumentStatus;
  /**
   * Backend filing lifecycle. Absent on presentational/mock documents that have
   * never been through the backend.
   */
  lifecycle?: DocumentLifecycle;
  modified: string;
  /** Global attribute values, keyed by attribute key. */
  attrs: GlobalAttrValues;
  /** Space-local attribute values, keyed by attribute name. */
  local: SpaceLocalValues;
  /**
   * Picked ids for id-shaped attributes (reference / select), keyed the same way
   * as {@link attrs} / {@link local} (global `key` or space-local name). Present
   * only for backend-read documents whose attribute carried `valueIds` (DV-466);
   * the edit form round-trips these back into the update mutation's `valueIds`.
   */
  attrIds?: Record<string, string[]>;
  /**
   * Tri-state per attribute (DV-503/DV-504), keyed like {@link attrs} /
   * {@link local} (global `key` or space-local name). Key present ⇒ the
   * attribute is bound to the document's space (`"set"` has a value,
   * `"empty"` is bound but blank — render "—"). Key absent while the map is
   * present ⇒ the attribute is not bound to that space — render "N/A". Map
   * absent entirely ⇒ presentational/mock document with no tri-state info;
   * every blank cell renders "—" as before.
   */
  attrStates?: Record<string, "set" | "empty">;
  versions: DocumentVersion[];
};

/** A product domain the Vault runs across (Fund Admin, Fay, DEX, Agency). */
export type VaultDomain = {
  id: string;
  name: string;
  /** 2-letter mark, e.g. "FA". */
  short: string;
  role: string;
};

/**
 * How an attribute is captured. Global attributes use Reference / Single-select /
 * Date; space-local attributes may use any of these values.
 */
export type AttrDataType =
  "Reference" | "Single-select" | "Multi-select" | "Date" | "Text" | "Number" | "Yes/No";

/**
 * A global attribute's provenance:
 * - `eng`  — Engineering-validated against a source of truth (register, records,
 *            calendar, or an eng-maintained pick-list). Stewards cannot mint these.
 * - `user` — Steward-created via the "New global attribute" workflow; a discrete
 *            list, not validated against a source of truth.
 */
export type GlobalAttrFlavor = "eng" | "user";

/** A curated, steward-governed global attribute — the shared spine referenced by spaces. */
export type GlobalAttribute = {
  key: string;
  name: string;
  flavor: GlobalAttrFlavor;
  dataType: AttrDataType;
  /** What the attribute records (help text). */
  captures: string;
  /** The source of truth it validates against, or null for user-created lists. */
  validates: string | null;
  /** Fixed value set, when the attribute has one; otherwise null (validated externally). */
  values: string[] | null;
  /**
   * Backend option id for each entry in {@link values}, keyed by display value
   * (`AttributeValueOption.id`). Populated only for `Single-select` /
   * `Multi-select` attributes — the backend requires a picked option's id, not
   * its label, in `valueIds` (DV-466/DV-544); `valueText` is rejected for
   * these types.
   */
  optionIdsByValue?: Record<string, string>;
};

/** A space-local attribute — created freely by a configurator, scoped to one space. */
export type SpaceLocalAttribute = {
  id: string;
  name: string;
  /**
   * Stable machine slug (the backend attribute `name`). Fixed at creation and
   * referenced by reporting/integrations — it never changes on rename. Absent
   * only for a not-yet-persisted local being authored in the editor.
   */
  slug?: string;
  dataType: AttrDataType;
  required?: boolean;
  default?: string;
  options?: string[];
  /** Backend option id for each entry in {@link options}, keyed by display value. See {@link GlobalAttribute.optionIdsByValue}. */
  optionIdsByValue?: Record<string, string>;
};

/** A space actively binding a same-named attribute, as reported by the promotion pre-check. */
export type PromotionConflictSpace = {
  id: string;
  name: string;
  domain: string;
};

/**
 * One holder of the label being promoted (DV-499). `spaces` may be empty —
 * global holders and space-locals not bound to any space report no spaces.
 */
export type PromotionLabelConflict = {
  attributeId: string;
  kind: "global_system" | "global_steward" | "space_local";
  label: string;
  spaces: PromotionConflictSpace[];
};

/** Why a promotion cannot proceed as-is. Only LABEL_CONFLICT is fixable in the modal (rename). */
export type PromotionBlocker =
  "LABEL_CONFLICT" | "NOT_SPACE_LOCAL" | "DEPRECATED" | "EMPTY_PICKLIST";

/** UI-facing result of the `attributePromotionPreflight` query (DV-499). */
export type PromotionPreflightSummary = {
  /** True ⟺ {@link blockers} is empty. */
  promotable: boolean;
  blockers: PromotionBlocker[];
  /** Non-empty ⟺ LABEL_CONFLICT is among {@link blockers}. */
  conflicts: PromotionLabelConflict[];
};

/** A space's reference to a global attribute: on/off plus per-space required/default. */
export type GlobalRef = {
  /** Whether the space references this global attribute. */
  on: boolean;
  /** Whether the space requires it before a document can be filed. */
  required: boolean;
  default: string;
};

/** A space's quality guarantee. `ingested` = un-guaranteed, accept-and-quarantine. */
export type SpaceQuality = "guaranteed" | "ingested";

/** A space's governing contract — how documents are permissioned, validated, and aged. */
export type SpaceContract = {
  permission: string;
  quality: SpaceQuality;
  qualityNote: string;
  lifecycle: string;
  /** How documents arrive (human-readable). */
  ingest: string;
  /** {@link Channel} id that routes documents here, or null for manual spaces. */
  channel: string | null;
};

/** A bounded context that owns a set of attributes, a contract, and a permission scope. */
export type Space = {
  id: string;
  name: string;
  /** {@link VaultDomain} id. */
  domain: string;
  contract: SpaceContract;
  /** Referenced global attributes, keyed by {@link GlobalAttribute.key}. */
  globals: Record<string, GlobalRef>;
  /** Space-local attributes, in display order. */
  locals: SpaceLocalAttribute[];
  /** Client workspaces this space is scoped to (client-based access), when set. */
  clients?: string[];
  /**
   * True when the space covers every client, false when it is scoped to
   * {@link clients}. Optional because it is unknown for a space that never came
   * from the API (drafts, mocks, fixtures).
   *
   * Distinct from `!clients?.length`: a scoped space whose client list happens
   * to be empty is NOT firm-wide. DV-501 gates the add-client and convert
   * controls on this flag, so the two cases must stay tellable apart.
   *
   * Setting it is a one-way door on the backend (DV-500) — a space that applies
   * to all clients can never be re-scoped.
   */
  appliesToAllClients?: boolean;
  /** Display names for {@link clients}, keyed by client id. Falls back to the id when absent. */
  clientNames?: Record<string, string>;
  /** Canopy roles this space is scoped to (role-based access), when set. */
  roles?: string[];
};

/** How a channel lands documents into a space. */
export type ChannelKind = "workflow" | "system" | "migration";

/**
 * Provenance routing: a channel lands documents in a pre-configured space
 * automatically, or pre-fills a space from workflow context.
 */
export type Channel = {
  id: string;
  name: string;
  kind: ChannelKind;
  /** {@link Space} id documents on this channel are routed to. */
  space: string;
  detail: string;
};

/** An untyped/incomplete upload held in accept-and-quarantine state. */
export type QuarantineItem = {
  id: string;
  file: string;
  name: string;
  /** Short reason it can't be filed, e.g. "Missing: Reporting Period". */
  problem: string;
  /** Longer explanation of the problem. */
  detail: string;
  arrived: string;
  /** {@link Space} id it was routed to, or null when no space matched. */
  space: string | null;
};

/** How an attribute value is captured on an upload form. */
export type FieldKind = "select" | "date" | "text";

/** Control-catalog entry: how an attribute key is captured and where its options come from. */
export type FieldDef = {
  label: string;
  kind: FieldKind;
  /** Reference-list id for `select` fields sourced from a shared list (see {@link ReferenceData}). */
  source?: string;
  /** Inline option set for `select` fields with a fixed value set. */
  options?: string[];
};

/** Shared reference lists that resolve `Reference` attributes and populate upload selects. */
export type ReferenceData = {
  clients: string[];
  /** Entities keyed by client name. */
  entities: Record<string, string[]>;
  investors: string[];
  periods: string[];
  fundAdmins: string[];
  assetClasses: string[];
  /** Canopy access roles, for role-scoped spaces. */
  roles: string[];
};

/** A grant that gives someone access to a document. */
export type AccessGrant = {
  name: string;
  /** How access was granted: a live rule, a frozen collection, a direct user, or internal staff. */
  kind: "predicate" | "collection" | "user" | "internal";
  /** Human explanation, e.g. "Matches LP / Investor = CalPERS · External". */
  detail: string;
};

export type ShareAction =
  "rule_created" | "collection_shared" | "docs_added_rule" | "docs_added_collection";

/** A single predicate condition within a live-rule share, e.g. `Status = External`. */
export type ShareCondition = { a: string; v: string };

/** Payload emitted when a user shares documents (the input to a share action). */
export type ShareEvent = {
  action: ShareAction;
  client: string;
  /** Human description of what was shared (rule expression or collection name). */
  target: string;
  recipient: string;
  docCount: number;
  /** Conditions, for live-rule shares. */
  conds?: ShareCondition[];
  /** Document names, for collection shares. */
  docs?: string[];
};

/** A recorded share event in the audit log (a ShareEvent plus when/who). */
export type ShareLogEntry = ShareEvent & {
  id: string;
  date: string;
  time: string;
  who: string;
  role: string;
  /** True when the event was produced by an automatic live rule rather than a person. */
  auto?: boolean;
};

/**
 * The thirteen `can_*` capability strings DV-354 cuts over — the lowercased
 * wire form of `main`'s `GranularPermission` enum members in
 * `DOC_VAULT_GRANULAR_PERMISSIONS` (`jsq/lib/granular_permissions/permissions.py`).
 * `can_move_vault_documents` and `can_archive_vault_space` have no backing
 * enum member (reserved, no mutation exists yet), so there is nothing to
 * lower-case those two from.
 */
export type VaultCapability =
  | "can_view_doc_vault_documents"
  | "can_download_doc_vault_documents"
  | "can_upload_doc_vault_documents"
  | "can_classify_doc_vault_documents"
  | "can_replace_doc_vault_document_version"
  | "can_delete_doc_vault_documents"
  | "can_view_doc_vault_spaces"
  | "can_create_doc_vault_space"
  | "can_edit_doc_vault_space_settings"
  | "can_manage_doc_vault_space_role_assignment"
  | "can_view_doc_vault_document_versions"
  | "can_view_doc_vault_global_attribute_schema"
  | "can_manage_doc_vault_global_attribute_definitions";

/** The signed-in user; `isSteward` gates the curated global-attribute affordances. */
export type CurrentUser = {
  name: string;
  role: string;
  isSteward: boolean;
  /**
   * The caller's granted `can_*` capabilities (DV-354, DV-693 §5). Undefined for
   * a caller the host app hasn't wired yet — under `documentVaultGranularAuth`,
   * that reads as "no capabilities", not "all of them" (fail closed).
   */
  capabilities?: VaultCapability[];
};
